import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { servicesAPI } from '../api';
import { useToast } from '../hooks/use-toast';
import { clearSavedForm, FORM_TYPE_BY_SERVICE } from '../components/forms/formUtils';
import SEO from '../components/SEO';

import CompanyRegistrationForm from '../components/forms/CompanyRegistrationForm';
import TaxClearanceForm from '../components/forms/TaxClearanceForm';
import DirectorChangesForm from '../components/forms/DirectorChangesForm';
import AnnualReturnsForm from '../components/forms/AnnualReturnsForm';
import BeneficialOwnershipForm from '../components/forms/BeneficialOwnershipForm';
import PublicOfficerForm from '../components/forms/PublicOfficerForm';
import NpcRegistrationForm from '../components/forms/NpcRegistrationForm';
import TrademarkForm from '../components/forms/TrademarkForm';
import BeeAffidavitForm from '../components/forms/BeeAffidavitForm';
import DocumentRequestForm from '../components/forms/DocumentRequestForm';
import EnquiryForm from '../components/forms/EnquiryForm';
import GenericServiceForm from '../components/forms/GenericServiceForm';

const FORM_COMPONENTS = {
  'company-registration': CompanyRegistrationForm,
  'personal-liability': CompanyRegistrationForm, // shares same flow
  'npc-registration': NpcRegistrationForm,
  'tax-clearance': TaxClearanceForm,
  'director-changes': DirectorChangesForm,
  'annual-returns': AnnualReturnsForm,
  'beneficial-ownership': BeneficialOwnershipForm,
  'public-officer': PublicOfficerForm,
  'trademark-registration': TrademarkForm,
  'bee-certificate': BeeAffidavitForm,
  'restore-company': DocumentRequestForm,
  'paye-uif': DocumentRequestForm,
  'csd-registration': DocumentRequestForm,
};

const ServiceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await servicesAPI.getById(id);
        if (alive) setService(data);
      } catch {
        if (alive) {
          toast({
            title: 'Service not found',
            description: 'Please try again.',
            variant: 'destructive',
          });
          navigate('/');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, navigate, toast]);

  const handleComplete = (formData) => {
    const formType = FORM_TYPE_BY_SERVICE[id] || id;
    try {
      sessionStorage.setItem(
        'consultium:pendingOrder',
        JSON.stringify({ service, formData, formType }),
      );
    } catch (err) {
      console.warn('[ServiceForm] could not persist pending order to sessionStorage', err);
    }
    clearSavedForm(id);
    navigate('/checkout', { state: { service, formData, formType } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading form...</p>
        </div>
        <Footer />
      </div>
    );
  }
  if (!service) return null;

  // Enquiry-only services skip checkout entirely
  if (service.enquiryOnly) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="service-form-page">
        <Navbar />
        <EnquiryForm service={service} />
        <Footer />
      </div>
    );
  }

  const FormComponent = FORM_COMPONENTS[id] || null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="service-form-page">
      <SEO
        title={`Apply — ${service.name} | Consultium Group`}
        description={`Complete your ${service.name} application online. We'll process and confirm within 24 hours.`}
        path={`/service/${id}/form`}
      />
      <Navbar />
      {FormComponent ? (
        <FormComponent
          serviceId={id}
          serviceName={service.name}
          onComplete={handleComplete}
        />
      ) : (
        <GenericServiceForm
          serviceId={id}
          serviceName={service.name}
          onComplete={handleComplete}
        />
      )}
      <Footer />
    </div>
  );
};

export default ServiceForm;
